// Lib
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { trim, isString } from 'lodash/fp';

// Utils
import { getTitle } from '../../../common/elements/utils/elementPropertyUtils';
import { preventOrphanedWords, stripOrphanedWordsSpecialCharacters } from '../../../common/utils/stringUtil';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { isNotElementEditorSheet } from '../../mobile/structural/sheet/utils/sheetStateUtils';

// Actions
import { getIsCurrentlyEditingThisElement } from '../../element/selection/currentlyEditingSelector';
import { updateElement } from '../../element/actions/elementActions';
import {
    startEditingElement,
    startEditingElementWithDelay,
    finishEditingElement,
    deselectAllElements,
} from '../../element/selection/selectionActions';

// Selectors
import { getAnyMatchingSheetOpen } from '../../mobile/structural/sheet/sheetSelectors';

// Components
import ElementSimpleContentEditable from './ElementSimpleContentEditable';

// Types
import { MNElement } from '../../../common/elements/elementModelTypes';

// Styles
import './EditableTitle.scss';

const DEFAULT_MAX_LENGTH = 100;

type EditableTitleProps = {
    element: MNElement;
    elementId: string;
    initialValue?: string;
    editorKey?: string;
    onStopEditing?: (params: { keepSelection?: boolean }) => void;
    onUpdate?: (params: { title: string; transactionId: string }) => void;
    wrapTitleElement?: React.ReactNode;
    maxLength?: number;
    isEditable?: boolean;
    isSelected?: boolean;
    selectFirst?: boolean;
    selectAllOnEdit?: boolean;
    editDelay?: number;
    filterQuery?: string;
};

const EditableTitle: React.FC<EditableTitleProps> = (props) => {
    const {
        element,
        elementId,
        initialValue,
        editorKey,
        onStopEditing: onStopEditingCallbackFn,
        onUpdate: onUpdateCallbackFn,
        wrapTitleElement,
        maxLength = DEFAULT_MAX_LENGTH,
        isEditable,
        isSelected,
        selectFirst,
        selectAllOnEdit,
        editDelay,
        filterQuery,
    } = props;

    const title = getTitle(element) || initialValue;

    const isEditing = useSelector((state: Immutable.Map<string, object>) =>
        getIsCurrentlyEditingThisElement(state, { elementId, editorKey }),
    );
    const isNonEditorSheetOpen = useSelector(getAnyMatchingSheetOpen(isNotElementEditorSheet));
    const showEditing = isEditable && isEditing;

    const dispatch = useDispatch();
    const dispatchStartEditing = useCallback(
        ({ editorFocusRect }: { editorFocusRect?: DOMRect } = {}) => {
            editDelay
                ? dispatch(
                      startEditingElementWithDelay({
                          id: elementId,
                          editorKey,
                          editDelay,
                          editorFocusRect,
                          editorId: undefined,
                          transactionId: undefined,
                      }),
                  )
                : dispatch(
                      (
                          startEditingElement as (params: {
                              id: string;
                              editorKey?: string;
                              editorFocusRect?: DOMRect | undefined;
                          }) => (dispatch: Function, getState: Function) => void
                      )({
                          id: elementId,
                          editorKey,
                          editorFocusRect,
                      }),
                  );
        },
        [elementId, editDelay, editorKey],
    );

    const dispatchStopEditing = useCallback(
        ({ keepSelection }: { keepSelection?: boolean }) => {
            if (onStopEditingCallbackFn) onStopEditingCallbackFn({ keepSelection });

            dispatch(finishEditingElement(elementId));
            if (!keepSelection) {
                dispatch(deselectAllElements());
            }
        },
        [elementId, onStopEditingCallbackFn],
    );

    const onUpdate = useCallback(
        (newTitle: string) => {
            if (!newTitle || !newTitle.length) return;

            if (onUpdateCallbackFn)
                return onUpdateCallbackFn({ title: newTitle, transactionId: String(getNewTransactionId()) });

            const updatedTitle = stripOrphanedWordsSpecialCharacters(newTitle);

            if (updatedTitle == title) return;
            dispatch(
                updateElement({
                    id: elementId,
                    changes: { title: updatedTitle },
                    transactionId: getNewTransactionId(),
                }),
            );
        },
        [onUpdateCallbackFn, title, elementId],
    );

    const cleanContent = useCallback(
        (content: string | undefined) => {
            if (!content) return;

            const contentString = isString(content) ? content : String(content);

            return preventOrphanedWords(trim(contentString.substring(0, maxLength)));
        },
        [maxLength],
    );

    useEffect(() => {
        if (!isNonEditorSheetOpen || !showEditing) return;

        dispatchStopEditing({ keepSelection: true });
    }, [isNonEditorSheetOpen]);

    return (
        <div className={classNames('EditableTitle', { editing: showEditing })}>
            <ElementSimpleContentEditable
                multiline
                className="editable-title"
                enterKeyHint="Done"
                isEditing={isEditing}
                isEditable={isEditable}
                isSelected={isSelected}
                selectAllOnEdit={selectAllOnEdit}
                selectFirst={selectFirst}
                wrapContent={wrapTitleElement}
                maxLength={maxLength}
                filterQuery={filterQuery}
                onStartEditing={dispatchStartEditing}
                onStopEditing={dispatchStopEditing}
                onUpdate={onUpdate}
                cleanContent={cleanContent}
                isInitialValue={title === initialValue}
            >
                {title}
            </ElementSimpleContentEditable>
        </div>
    );
};

export default EditableTitle;
